import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  BarChart3,
  Check,
  Gift,
  Hammer,
  LockKeyhole,
  Shirt,
  ShoppingBasket,
  Sparkles,
  UsersRound,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ConnectForm from "../components/ConnectForm";
import usePageMetadata from "../lib/usePageMetadata";

const outcomeIcons = [Sparkles, BarChart3, LockKeyhole];
const useCaseIcons = [ShoppingBasket, Sparkles, Shirt, Hammer, Gift, UsersRound];

export default function Enterprise() {
  const { t } = useTranslation();

  const outcomes = [1, 2, 3].map((number, index) => ({
    title: t(`enterprise.outcome${number}Title`),
    body: t(`enterprise.outcome${number}Body`),
    icon: outcomeIcons[index],
  }));
  const useCases = [1, 2, 3, 4, 5, 6].map((number, index) => ({
    title: t(`enterprise.useCase${number}Title`),
    body: t(`enterprise.useCase${number}Body`),
    icon: useCaseIcons[index],
  }));
  const pilotItems = [1, 2, 3, 4, 5, 6].map((number) =>
    t(`enterprise.pilotItem${number}`),
  );
  const steps = [1, 2, 3].map((number) => ({
    label: t(`enterprise.step${number}Label`),
    title: t(`enterprise.step${number}Title`),
    body: t(`enterprise.step${number}Body`),
  }));
  const faqs = [1, 2, 3, 4].map((number) => ({
    question: t(`enterprise.faq${number}Question`),
    answer: t(`enterprise.faq${number}Answer`),
  }));

  usePageMetadata({
    title: t("enterprise.metaTitle"),
    description: t("enterprise.metaDescription"),
    path: "/enterprise",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: t("enterprise.kicker"),
        description: t("enterprise.metaDescription"),
        url: "https://trubeaute.com/enterprise",
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "TruBeauté Enterprise",
        provider: {
          "@type": "Organization",
          name: "TruBeauté",
          url: "https://trubeaute.com",
        },
        description: t("enterprise.metaDescription"),
        areaServed: "Worldwide",
      },
    ],
  });

  return (
    <div className="min-h-screen trubeaute-gradient">
      <Header />
      <main className="pt-28 sm:pt-32 pb-24" data-testid="enterprise-page">
        <section className="px-6 sm:px-10">
          <div className="relative max-w-6xl mx-auto overflow-hidden rounded-[2.25rem] border border-[#E4D2CE] bg-[#2C2123] px-7 py-14 sm:px-12 sm:py-20 lg:px-16">
            <div className="absolute -right-20 -top-28 h-80 w-80 rounded-full bg-[#D5A8A4]/20 blur-3xl" />
            <div className="absolute -bottom-28 left-1/3 h-64 w-64 rounded-full bg-[#9B6A6C]/20 blur-3xl" />
            <div className="relative max-w-4xl mx-auto text-center">
              <h1 className="font-serif text-[2.55rem] sm:text-6xl lg:text-7xl leading-[1.02] tracking-[-0.02em] break-words text-white mb-7">
                {t("enterprise.headline")}
              </h1>
              <p className="text-lg sm:text-xl leading-8 text-[#F3E5E1] max-w-3xl mx-auto mb-9">
                {t("enterprise.subline")}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="#enterprise-inquiry"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#E7BDB8] px-7 py-3.5 font-medium text-[#2C2123] hover:bg-white transition-colors"
                  data-testid="enterprise-primary-cta"
                >
                  {t("enterprise.primaryCta")} <ArrowRight className="h-4 w-4" />
                </a>
                <Link
                  to="/partners"
                  className="inline-flex items-center justify-center rounded-full border border-white/30 bg-white/5 px-7 py-3.5 text-white hover:bg-white/10 transition-colors"
                >
                  {t("enterprise.partnerCta")}
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="px-6 sm:px-10 py-20">
          <div className="max-w-6xl mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-11">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#2C2123] mb-5">
                {t("enterprise.outcomesTitle")}
              </h2>
              <p className="text-[#5C4B51] leading-8">
                {t("enterprise.outcomesBody")}
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {outcomes.map(({ title, body, icon: Icon }) => (
                <article key={title} className="rounded-3xl border border-[#EADDD7] bg-white/80 p-7 soft-shadow">
                  <div className="h-11 w-11 rounded-2xl bg-[#F2E8E9] flex items-center justify-center mb-5">
                    <Icon className="h-5 w-5 text-[#9B6A6C]" />
                  </div>
                  <h3 className="font-serif text-2xl text-[#2C2123] mb-3">{title}</h3>
                  <p className="text-[#5C4B51] leading-7">{body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="px-6 sm:px-10 pb-20">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-[0.82fr_1.18fr] gap-9 lg:gap-14 items-start">
            <div className="lg:sticky lg:top-28">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#2C2123] mb-5">
                {t("enterprise.useCasesTitle")}
              </h2>
              <p className="text-[#5C4B51] leading-8">
                {t("enterprise.useCasesBody")}
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              {useCases.map(({ title, body, icon: Icon }) => (
                <article key={title} className="rounded-3xl border border-[#EADDD7] bg-[#FDF8F5] p-7">
                  <Icon className="h-6 w-6 text-[#9B6A6C] mb-5" />
                  <h3 className="font-serif text-2xl text-[#2C2123] mb-3">{title}</h3>
                  <p className="text-[#5C4B51] leading-7">{body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="px-6 sm:px-10 pb-20">
          <div className="max-w-6xl mx-auto rounded-[2rem] bg-[#F1DED8] p-7 sm:p-10 lg:p-12 grid lg:grid-cols-2 gap-10 lg:gap-14">
            <div>
              <h2 className="font-serif text-4xl text-[#2C2123] mb-5">
                {t("enterprise.pilotTitle")}
              </h2>
              <p className="text-[#5C4B51] leading-8">
                {t("enterprise.pilotBody")}
              </p>
            </div>
            <ul className="grid sm:grid-cols-2 gap-x-7 gap-y-5 content-start">
              {pilotItems.map((item) => (
                <li key={item} className="flex gap-3 text-[#3C2F33] leading-6">
                  <span className="mt-0.5 h-6 w-6 rounded-full bg-white/80 flex items-center justify-center shrink-0">
                    <Check className="h-3.5 w-3.5 text-[#9B6A6C]" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="px-6 sm:px-10 pb-20">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-11">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#2C2123]">
                {t("enterprise.processTitle")}
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {steps.map((step) => (
                <article key={step.label} className="relative rounded-3xl border border-[#EADDD7] bg-white/75 p-7">
                  <span className="text-xs uppercase tracking-[0.2em] text-[#9B6A6C]">{step.label}</span>
                  <h3 className="font-serif text-2xl text-[#2C2123] mt-3 mb-3">{step.title}</h3>
                  <p className="text-[#5C4B51] leading-7">{step.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="px-6 sm:px-10 pb-20">
          <div className="max-w-5xl mx-auto grid lg:grid-cols-[0.82fr_1.18fr] gap-10 items-start">
            <div>
              <h2 className="font-serif text-4xl text-[#2C2123] mb-5">
                {t("enterprise.faqTitle")}
              </h2>
              <p className="text-[#5C4B51] leading-7">{t("enterprise.faqBody")}</p>
            </div>
            <div className="divide-y divide-[#EADDD7] border-y border-[#EADDD7]">
              {faqs.map((faq) => (
                <details key={faq.question} className="group py-5">
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-4 font-medium text-[#2C2123]">
                    {faq.question}
                    <span className="text-[#9B6A6C] text-xl group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="text-[#5C4B51] leading-7 pt-3 pr-8">{faq.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section id="enterprise-inquiry" className="px-6 sm:px-10 scroll-mt-28">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-serif text-4xl sm:text-5xl text-[#2C2123] mb-4">
                {t("enterprise.formTitle")}
              </h2>
              <p className="text-[#5C4B51] leading-7 max-w-2xl mx-auto">
                {t("enterprise.formBody")}
              </p>
            </div>
            <ConnectForm
              initialRole="enterprise"
              messagePlaceholderKey="enterprise.formMessagePlaceholder"
              submitLabelKey="enterprise.formSubmit"
            />
            <p className="text-sm text-[#6F6065] text-center mt-6">
              {t("enterprise.formEmailFallback")}{" "}
              <a
                href="mailto:iyal@trubeaute.com?subject=TruBeaut%C3%A9%20Enterprise"
                className="text-[#805557] underline font-medium"
              >
                iyal@trubeaute.com
              </a>
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
